import { ProcedureDocumentType, WaypointType, InstructionType, YesNoOptionLabel, DbRef } from '@caresend/types';
import { getProcedureDocumentsByType, isNullish, formatFullName, formatPhoneNumber, formatDateOfBirth, formatTimeZoneDateSimple, procedureDateInfo, formatAddressUS, AddressFormatType, getInstruction, nullishFilter, capitalize } from '@caresend/utils';
import { defineComponent, computed } from 'vue';
import __vue_component__ from '../../../../../../cards/BasicCard/BasicCard.vue.js';
import { usePortalStore } from '../../../../store/portal/index.js';
import { getStore } from '../../../../../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'ManualReqFormInfo',
    components: {
        BasicCard: __vue_component__,
    },
    props: {
        procedureID: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const store = getStore();
        const portalStore = usePortalStore();
        const procedure = computed(() => portalStore.procedures.get(props.procedureID));
        const instructionalRequisitionForm = computed(() => {
            var _a, _b, _c;
            return (_c = (_b = getProcedureDocumentsByType((_a = procedure.value) !== null && _a !== void 0 ? _a : undefined, ProcedureDocumentType.INSTRUCTIONAL_REQUISITION_FORM)) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.file;
        });
        const booking = computed(() => { var _a; return (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.computed.booking; });
        const patient = computed(() => {
            var _a;
            if (isNullish(booking.value)
                || isNullish(booking.value.patientID))
                return;
            return (_a = store.getters['users/getUserByID'](booking.value.patientID)) !== null && _a !== void 0 ? _a : undefined;
        });
        const patientName = computed(() => {
            if (patient.value === undefined)
                return;
            return formatFullName(patient.value);
        });
        const patientPhone = computed(() => { var _a, _b; return formatPhoneNumber((_b = (_a = patient.value) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.phone); });
        const patientDOB = computed(() => {
            var _a, _b;
            if (!((_b = (_a = patient.value) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.dateOfBirth))
                return;
            return formatDateOfBirth(patient.value.info.dateOfBirth);
        });
        const waypoint = computed(() => {
            var _a, _b, _c;
            return (_c = Object.values((_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.computed.waypoints) !== null && _b !== void 0 ? _b : {})
                .find((wp) => wp.type === WaypointType.PATIENT)) !== null && _c !== void 0 ? _c : null;
        });
        const collectionDate = computed(() => {
            var _a, _b, _c;
            return (_b = formatTimeZoneDateSimple((_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.date)) !== null && _b !== void 0 ? _b : procedureDateInfo((_c = procedure.value) === null || _c === void 0 ? void 0 : _c.dateInfo);
        });
        const fastingRequired = computed(() => {
            var _a, _b, _c;
            return (_c = (_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.instructions) === null || _b === void 0 ? void 0 : _b.some((instr) => instr.type === InstructionType.FASTING && instr.instruction !== YesNoOptionLabel.NO)) !== null && _c !== void 0 ? _c : false;
        });
        const patientAddress = computed(() => {
            var _a, _b, _c, _d, _e, _f;
            if (!((_c = (_b = (_a = patient.value) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.address) === null || _c === void 0 ? void 0 : _c.addressComponents)) {
                if (!((_d = waypoint.value) === null || _d === void 0 ? void 0 : _d.location))
                    return;
                return formatAddressUS(waypoint.value.location, undefined, AddressFormatType.MULTI_LINE);
            }
            return formatAddressUS((_f = (_e = patient.value) === null || _e === void 0 ? void 0 : _e.info) === null || _f === void 0 ? void 0 : _f.address, undefined, AddressFormatType.MULTI_LINE);
        });
        const placeAccount = computed(() => {
            var _a, _b, _c;
            const id = (_c = Object.keys((_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.placeAccounts) !== null && _b !== void 0 ? _b : {})) === null || _c === void 0 ? void 0 : _c[0];
            if (isNullish(id))
                return null;
            return portalStore[DbRef.PLACE_ACCOUNTS].getRaw(id);
        });
        const placeAccountOfficeAddress = computed(() => {
            var _a, _b;
            if (!((_b = (_a = placeAccount.value) === null || _a === void 0 ? void 0 : _a.officeInfo) === null || _b === void 0 ? void 0 : _b.address))
                return;
            return formatAddressUS(placeAccount.value.officeInfo.address, undefined, AddressFormatType.MULTI_LINE);
        });
        const placeGroup = computed(() => {
            var _a, _b, _c, _d;
            if ((_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.placeID) {
                const { placeID } = waypoint.value;
                const place = portalStore[DbRef.PLACES].getRaw(placeID);
                if (place === null || place === void 0 ? void 0 : place.placeGroupID) {
                    const { placeGroupID } = place;
                    return portalStore[DbRef.PLACE_GROUPS].getRaw(placeGroupID);
                }
            }
            if ((_b = placeAccount.value) === null || _b === void 0 ? void 0 : _b.privatePlaceGroup)
                return placeAccount.value.privatePlaceGroup;
            if (!((_c = placeAccount.value) === null || _c === void 0 ? void 0 : _c.placeGroupID))
                return null;
            return portalStore[DbRef.PLACE_GROUPS].getRaw((_d = placeAccount.value) === null || _d === void 0 ? void 0 : _d.placeGroupID);
        });
        const policyHolderDOB = computed(() => {
            var _a;
            if (!patient.value || !((_a = patient.value) === null || _a === void 0 ? void 0 : _a.insuranceInfo) || !patient.value.insuranceInfo[0])
                return null;
            const insuranceInfo = patient.value.insuranceInfo[0];
            if (!insuranceInfo.policyHolderInfo || !(insuranceInfo === null || insuranceInfo === void 0 ? void 0 : insuranceInfo.policyHolderInfo.dateOfBirth))
                return null;
            return formatDateOfBirth(insuranceInfo.policyHolderInfo.dateOfBirth);
        });
        const getPolicyHolderDOB = (insuranceEntry) => {
            if (!(insuranceEntry === null || insuranceEntry === void 0 ? void 0 : insuranceEntry.policyHolderInfo))
                return patientDOB.value;
            return formatDateOfBirth(insuranceEntry.policyHolderInfo.dateOfBirth);
        };
        const getPolicyHolderAddress = (insuranceEntry) => {
            if (!insuranceEntry.policyHolderInfo)
                return [];
            return formatAddressUS(insuranceEntry.policyHolderInfo.address, undefined, AddressFormatType.MULTI_LINE);
        };
        const getPolicyHolderName = (insuranceEntry) => {
            if (!insuranceEntry.policyHolderInfo)
                return;
            return `${insuranceEntry.policyHolderInfo.firstName} ${insuranceEntry.policyHolderInfo.lastName}`;
        };
        const prescriber = computed(() => { var _a; return (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.computed.prescriber; });
        const prescriberName = computed(() => {
            if (!prescriber.value)
                return null;
            return formatFullName(prescriber.value);
        });
        const diagnosis = computed(() => {
            var _a, _b;
            return (_b = getInstruction((_a = procedure.value) === null || _a === void 0 ? void 0 : _a.instructions, InstructionType.DIAGNOSIS)) === null || _b === void 0 ? void 0 : _b.instruction;
        });
        const products = computed(() => {
            var _a, _b;
            const productIDs = Object.keys((_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.products) !== null && _b !== void 0 ? _b : {});
            return productIDs.map((productID) => { var _a; return (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.products[productID]; }).filter(nullishFilter);
        });
        const getPlaceGroupConfig = (product) => {
            var _a, _b, _c;
            const placeGroupID = (_a = placeGroup.value) === null || _a === void 0 ? void 0 : _a.id;
            if (!placeGroupID)
                return null;
            return (_c = (_b = product.placeGroups) === null || _b === void 0 ? void 0 : _b[placeGroupID]) !== null && _c !== void 0 ? _c : null;
        };
        /**
         * Returns product info based on procedure's place group.
         *
         * Examples:
         * - Matching place group config found:
         *   - `(LAB CODE) Lab friendly name (LAB CPT CODES, ...)`
         * - No matching place group config found:
         *   - `Product name`
         */
        const getProductInfo = (product) => {
            const placeGroupConfig = getPlaceGroupConfig(product);
            if (!placeGroupConfig)
                return product.name;
            const { labCode, cptCodes, friendlyName } = placeGroupConfig;
            return `(${labCode}) ${friendlyName} (${cptCodes.join(', ')})`;
        };
        const labInstructions = computed(() => {
            var _a, _b, _c;
            return (_c = (_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.instructions) === null || _b === void 0 ? void 0 : _b.find((instruction) => instruction.type === InstructionType.LAB)) === null || _c === void 0 ? void 0 : _c.instruction;
        });
        return {
            capitalize,
            collectionDate,
            diagnosis,
            fastingRequired,
            formatAddressUS,
            formatPhoneNumber,
            getPolicyHolderAddress,
            getPolicyHolderDOB,
            getPolicyHolderName,
            getProductInfo,
            instructionalRequisitionForm,
            labInstructions,
            patient,
            patientAddress,
            patientDOB,
            patientName,
            patientPhone,
            placeAccount,
            placeAccountOfficeAddress,
            placeGroup,
            policyHolderDOB,
            prescriber,
            prescriberName,
            procedure,
            products,
            waypoint,
        };
    },
});

export { script as default };
